import * as Api from '@ViewModels';
import { Button } from '../../components-v2/Button';
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogPopup, DialogTitle } from '../../components-v2/Dialog';
import { Input } from '../../components-v2/Input';
import { CountrySelector } from '../../components/ValueSelectors/CountrySelector';
import { INameValue } from '../../components/ValueSelectors/ValueSelector';
import { styleSheet } from './styles';
// eslint-disable-next-line import/no-internal-modules
import { yupResolver } from '@hookform/resolvers/yup';
import { css } from 'aphrodite';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

const validationSchema = yup
	.object({
		address1: yup.string().required('Address line 1 is required'),
		address2: yup.string(),
		city: yup.string().required('City is required'),
		country: yup.string().required('Country is required'),
		firstName: yup.string(),
		lastName: yup.string(),
		postalCode: yup.string().required('Postal Code is required'),
		stateProvince: yup.string().required('State/Province is required'),
	})
	.required();

interface IFormData {
	address1: string;
	address2?: string;
	city: string;
	country: string;
	firstName?: string;
	lastName?: string;
	postalCode: string;
	stateProvince: string;
}

export const ReturnContactModal = ({
	initialRecipient,
	isOpen,
	onSave,
	onCancel,
}: {
	initialRecipient?: Api.IHandwrittenCardReturnContact;
	onSave: (hwcRecipient: IFormData) => void;
	onCancel: () => void;
	isOpen: boolean;
}) => {
	const {
		handleSubmit,
		control,
		formState: { errors },
	} = useForm({
		defaultValues: {
			address1: initialRecipient?.address?.address1 ?? '',
			address2: initialRecipient?.address?.address2 ?? '',
			city: initialRecipient?.address?.city ?? '',
			country: initialRecipient?.address?.country ?? '',
			firstName: initialRecipient?.firstName ?? '',
			lastName: initialRecipient?.lastName ?? '',
			postalCode: initialRecipient?.address?.postalCode ?? '',
			stateProvince: initialRecipient?.address?.stateProvince ?? '',
		},
		resolver: yupResolver(validationSchema),
	});

	return (
		<Dialog dismissOnOutsideClick={false} onOpenChange={open => !open && onCancel()} open={isOpen}>
			<DialogPopup>
				<form onSubmit={handleSubmit(onSave)}>
					<DialogHeader>
						<DialogTitle>Add Return Address</DialogTitle>
					</DialogHeader>
					<DialogBody>
						<div className={css(styleSheet.addresslineGroup)}>
							<div className={css(styleSheet.addresslineCol)}>
								<label className={css(styleSheet.label)} htmlFor='address-modal-firstName-input'>
									First Name
								</label>
								<Controller
									name='firstName'
									control={control}
									render={({ field: { ref, ...fieldProps } }) => (
										<Input id='address-modal-firstName-input' type='text' ref={ref} {...fieldProps} />
									)}
								/>
							</div>
							<div className={css(styleSheet.addresslineCol)}>
								<label className={css(styleSheet.label)} htmlFor='address-modal-lastName-input'>
									Last Name
								</label>
								<Controller
									name='lastName'
									control={control}
									render={({ field: { ref, ...fieldProps } }) => (
										<Input id='address-modal-lastName-input' type='text' ref={ref} {...fieldProps} />
									)}
								/>
							</div>
						</div>

						<label className={css(styleSheet.label, styleSheet.labelRequired)} htmlFor='address-modal-address1-input'>
							Address Line 1
						</label>
						<Controller
							name='address1'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<Input
									id='address-modal-address1-input'
									type='text'
									placeholder='e.g. 123 Main St.'
									ref={ref}
									{...fieldProps}
								/>
							)}
						/>
						{errors.address1 != null ? (
							<p role='alert' className={css(styleSheet.formErrorLine)}>
								{errors.address1.message}
							</p>
						) : null}
						<label className={css(styleSheet.label)} htmlFor='address-modal-address2-input'>
							Address Line 2
						</label>
						<Controller
							name='address2'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<Input
									id='address-modal-address2-input'
									type='text'
									placeholder='e.g. Apt. 4B'
									ref={ref}
									{...fieldProps}
								/>
							)}
						/>
						<div className={css(styleSheet.addresslineGroup)}>
							<div className={css(styleSheet.addresslineCol)}>
								<label className={css(styleSheet.label, styleSheet.labelRequired)} htmlFor='address-modal-city-input'>
									City
								</label>
								<Controller
									name='city'
									control={control}
									render={({ field: { ref, ...fieldProps } }) => (
										<Input id='address-modal-city-input' type='text' ref={ref} {...fieldProps} />
									)}
								/>
								{errors.city != null ? (
									<p role='alert' className={css(styleSheet.formErrorLine)}>
										{errors.city.message}
									</p>
								) : null}
							</div>
							<div className={css(styleSheet.addresslineCol)}>
								<label
									className={css(styleSheet.label, styleSheet.labelRequired)}
									htmlFor='address-modal-stateProvince-input'
								>
									State/Province
								</label>
								<Controller
									name='stateProvince'
									control={control}
									rules={{ required: true }}
									render={({ field: { ref, ...fieldProps } }) => (
										<Input id='address-modal-stateProvince-input' type='text' ref={ref} {...fieldProps} />
									)}
								/>
								{errors.stateProvince != null ? (
									<p role='alert' className={css(styleSheet.formErrorLine)}>
										{errors.stateProvince.message}
									</p>
								) : null}
							</div>
							<div className={css(styleSheet.addresslineCol)}>
								<label
									className={css(styleSheet.label, styleSheet.labelRequired)}
									htmlFor='address-modal-postalCode-input'
								>
									Zip/Postal Code
								</label>
								<Controller
									name='postalCode'
									control={control}
									rules={{ required: true }}
									render={({ field: { ref, ...fieldProps } }) => (
										<Input id='address-modal-postalCode-input' type='text' ref={ref} {...fieldProps} />
									)}
								/>
								{errors.postalCode != null ? (
									<p role='alert' className={css(styleSheet.formErrorLine)}>
										{errors.postalCode.message}
									</p>
								) : null}
							</div>
						</div>
						<label className={css(styleSheet.label, styleSheet.labelRequired)} htmlFor='address-modal-country-input'>
							Country
						</label>
						<Controller
							name='country'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<CountrySelector
									onSelectionChanged={(countrySelection: INameValue) =>
										fieldProps.onChange(countrySelection.value !== 'None' ? countrySelection.value : '')
									}
									selectedValue={fieldProps.value}
								/>
							)}
						/>
						{errors.country != null ? (
							<p role='alert' className={css(styleSheet.formErrorLine)}>
								{errors.country.message}
							</p>
						) : null}
					</DialogBody>
					<DialogFooter>
						{/* Explicit type: an untyped button inside the form submits it, so Cancel used to save too. */}
						<Button onClick={onCancel} type='button' variant='secondary'>
							Cancel
						</Button>
						<Button type='submit'>Save</Button>
					</DialogFooter>
				</form>
			</DialogPopup>
		</Dialog>
	);
};
